import { Box, Typography } from "@mui/material";
import { RemediationScripts } from "~/components/DetailsPage/components/Remediation/RemediationScripts";
import { SectionHeading } from "~/components/DetailsPage/components";
import { AnimatePresence } from "motion/react";
import { Flex } from "~/components/Flex";
import { useRemediation } from "~/components/Remediation/hooks/useRemediation";
import { PACKAGES_TO_REMEDIATE } from "./constants";
import { Ecosystems } from "~/components/Remediation/Ecosystems/Ecosystems";
import { PackagesToInclude } from "~/components/Remediation/PackagesToInclude/PackagesToInclude";
import { IndividualPackages } from "~/components/Remediation/IndividualPackages/IndividualPackages";
import { RemediationEmptyState } from "~/components/Remediation/RemediationEmptyState/RemediationEmptyState";
import { REMEDIATION_SECTION_ID } from "~/components/Remediation/RemediationJumToButton/RemediationJumpToButton";
import { EmptySection } from "~/components/vulnerabilities";
import { FilePath } from "~/components/Remediation/FilePath/FilePath";
import {
  RemediationPackageFields,
  UiAction,
  UiPage,
  useRegisterUserActionMutation,
} from "~/operations";
import { useScope } from "~/hooks/useScope";

export interface RemediationProps {
  scopeMrn: string | undefined;
  findingId?: string;
  findingMrn?: string;
  isAssetContext?: boolean;
  packages?: RemediationPackageFields[];
  showHeader?: boolean;
  initiativeMrn?: string;
}

export function Remediation({
  scopeMrn,
  findingId,
  findingMrn,
  isAssetContext = false,
  packages = [],
  showHeader = true,
  initiativeMrn,
}: RemediationProps) {
  const { spaceScope } = useScope();
  const [registerUserAction] = useRegisterUserActionMutation();

  const {
    hideRemediationSection,
    isRemediationEmpty,
    remediationError,
    isLoading,
    ecosystemTitle,
    ecosystemVersion,
    selectedEcosystemsVersions,
    showEcosystems,
    showPackages,
    packagesType,
    fixAllPackagesLabel,
    remediationPackages,
    remediationScripts,
    remediationForScopeResponse,
    selectedPackages,
    ecosystems,
    handleEcosystemTitleChange,
    handleEcosystemVersionChange,
    handlePackagesChange,
    handlePackagesToIncludeClick,
  } = useRemediation({
    scopeMrn,
    findingId,
    findingMrn,
    isAssetContext,
    packages,
    initiativeMrn,
  });

  if (hideRemediationSection) {
    if (!findingMrn) return null;
    // Even without remediation data, show file path evidence if available
    return (
      <Box mt={2} mb={4}>
        <FilePath scopeMrn={scopeMrn} findingMrn={findingMrn} />
      </Box>
    );
  }

  const renderRemediationContent = () => {
    if (remediationError && !isLoading) {
      return (
        <EmptySection
          id="software-remediation-error"
          text="Failed to load remediation"
        />
      );
    }

    if (isRemediationEmpty && !isLoading) {
      return (
        <RemediationEmptyState remediation={remediationForScopeResponse} />
      );
    }

    return (
      <RemediationScripts
        scripts={remediationScripts}
        loading={isLoading}
        onCopy={handleCopyRemediation}
      />
    );
  };

  const handleCopyRemediation = () => {
    if (!findingId) return;

    registerUserAction({
      variables: {
        input: {
          action: UiAction.RemediationCopy,
          page: isAssetContext ? UiPage.FindingOnAsset : UiPage.Finding,
          scopeMrn: spaceScope?.mrn || "unknown",
          details: findingId,
        },
      },
    });
  };

  return (
    <Box component="form" id={REMEDIATION_SECTION_ID}>
      {showHeader && (
        <SectionHeading
          heading="Remediation"
          id="remediation-section-title"
          sx={isAssetContext ? { marginBottom: 0 } : {}}
        />
      )}
      <Box mt={2} mb={4}>
        <FilePath
          scopeMrn={scopeMrn}
          findingMrn={findingMrn}
          ecosystem={ecosystemVersion || ecosystemTitle}
        />
      </Box>
      <AnimatePresence>
        {showEcosystems && (
          <Flex key="ecosystems-section" flexDirection="column">
            <Ecosystems
              isLoading={isLoading}
              ecosystems={ecosystems}
              selectedEcosystemTitle={ecosystemTitle}
              selectedEcosystemVersion={ecosystemVersion}
              onEcosystemTitleChange={handleEcosystemTitleChange}
              onEcosystemVersionChange={handleEcosystemVersionChange}
              selectedEcosystemsVersions={selectedEcosystemsVersions}
            />
          </Flex>
        )}
        {showPackages && (
          <PackagesToInclude
            packagesType={packagesType}
            handlePackagesToIncludeClick={handlePackagesToIncludeClick}
            fixAllPackagesLabel={fixAllPackagesLabel}
            isLoading={isLoading}
          />
        )}
        {packagesType === PACKAGES_TO_REMEDIATE.INDIVIDUAL && showPackages && (
          <IndividualPackages
            remediationPackages={remediationPackages}
            selectedPackages={selectedPackages}
            handlePackagesChange={handlePackagesChange}
            isLoading={isLoading}
          />
        )}
        {renderRemediationContent()}
      </AnimatePresence>
    </Box>
  );
}
